import { UserForegroundDomainEnum } from './user-foreground-domain-enum'
import { CompletedStatusEnum } from './completed-status-enum'
import { ReviewedStatusEnum } from './reviewed-status-enum'
import { TaskReflectionSetting } from './task-reflection-setting'
import { ForegroundReflection } from './foreground-reflection'
import { TaskCompletedDetail } from './task-completed-detail'
import { Option } from './option'
import { QuestionnaireValueRes } from './questionnaire-value-res'
import { InterviewReflection } from './interview-reflection'

export interface TaskReflectionInterface {
  /**
   * 任务关联任务
   */
  taskId: number
  activityId: number
  rid: number
  reflectionDomain: UserForegroundDomainEnum
  completedStatus?: CompletedStatusEnum
  reviewedStatus?: ReviewedStatusEnum
  /**
   * 开始任务时间
   */
  startAt?: string | null
  /**
   * 结束任务时间
   */
  endAt?: string | null
  /**
   * 任务用时
   */
  duration?: number
  /**
   * 任务得分
   */
  gainedScore?: string
  /**
   * 任务得分率
   */
  gainedScoreRate?: string
  /**
   * 其它设置
   */
  setting?: TaskReflectionSetting
  reflection?: ForegroundReflection
  /**
   * 任务完成数量
   *  - 课程任务：子目录任务完成数
   *  - 其它任务：暂未使用
   */
  completedTaskSum?: number
  /**
   * 任务总数量
   *  - 课程任务：子目录任务总数
   *  - 其它任务：暂未使用
   */
  taskSum?: number
  /**
   * 任务完成详情
   *  - 课程任务：子目录任务完成详情
   *  - 报名任务：选择的选项
   *  - 测验任务：截屏时间、截屏图片
   *  - 其它任务：暂未使用
   */
  completedDetails?: TaskCompletedDetail[]
  /**
   * 报名任务
   *  - 选择的选项
   */
  enrollOptions?: Option[]
  /**
   * 问卷任务
   */
  questionnaire?: QuestionnaireValueRes[]
  /**
   * 测验任务
   *  - 本人抽题次数
   */
  quizBlockCount?: number | null
  /**
   * 面试任务
   *  - 本人面试结果
   */
  interviewResult?: InterviewReflection | null
  /**
   * 主键 ID
   */
  id: number
  /**
   * 学校 ID
   */
  schoolId: number
  /**
   * 创建时间
   */
  createdAt: string
  /**
   * 更新时间
   */
  updatedAt: string
}

export class TaskReflection implements TaskReflectionInterface {
  /**
   * 任务关联任务
   */
  taskId: number
  activityId: number
  rid: number
  reflectionDomain: UserForegroundDomainEnum
  completedStatus?: CompletedStatusEnum
  reviewedStatus?: ReviewedStatusEnum
  /**
   * 开始任务时间
   */
  startAt?: string | null
  /**
   * 结束任务时间
   */
  endAt?: string | null
  /**
   * 任务用时
   */
  duration?: number
  /**
   * 任务得分
   */
  gainedScore?: string
  /**
   * 任务得分率
   */
  gainedScoreRate?: string
  /**
   * 其它设置
   */
  setting?: TaskReflectionSetting
  reflection?: ForegroundReflection
  /**
   * 任务完成数量
   *  - 课程任务：子目录任务完成数
   *  - 其它任务：暂未使用
   */
  completedTaskSum?: number
  /**
   * 任务总数量
   *  - 课程任务：子目录任务总数
   *  - 其它任务：暂未使用
   */
  taskSum?: number
  /**
   * 任务完成详情
   *  - 课程任务：子目录任务完成详情
   *  - 报名任务：选择的选项
   *  - 测验任务：截屏时间、截屏图片
   *  - 其它任务：暂未使用
   */
  completedDetails?: TaskCompletedDetail[]
  /**
   * 报名任务
   *  - 选择的选项
   */
  enrollOptions?: Option[]
  /**
   * 问卷任务
   */
  questionnaire?: QuestionnaireValueRes[]
  /**
   * 测验任务
   *  - 本人抽题次数
   */
  quizBlockCount?: number | null
  /**
   * 面试任务
   *  - 本人面试结果
   */
  interviewResult?: InterviewReflection | null
  /**
   * 主键 ID
   */
  id: number
  /**
   * 学校 ID
   */
  schoolId: number
  /**
   * 创建时间
   */
  createdAt: string
  /**
   * 更新时间
   */
  updatedAt: string

  constructor(props: TaskReflectionInterface) {
    const {
      // 重命名变量以规避与 JS 保留词的冲突, 比如 default, with
      taskId: _taskId,
      activityId: _activityId,
      rid: _rid,
      reflectionDomain: _reflectionDomain,
      completedStatus: _completedStatus,
      reviewedStatus: _reviewedStatus,
      startAt: _startAt,
      endAt: _endAt,
      duration: _duration,
      gainedScore: _gainedScore,
      gainedScoreRate: _gainedScoreRate,
      setting: _setting,
      reflection: _reflection,
      completedTaskSum: _completedTaskSum,
      taskSum: _taskSum,
      completedDetails: _completedDetails,
      enrollOptions: _enrollOptions,
      questionnaire: _questionnaire,
      quizBlockCount: _quizBlockCount,
      interviewResult: _interviewResult,
      id: _id,
      schoolId: _schoolId,
      createdAt: _createdAt,
      updatedAt: _updatedAt,
      // 可能会有额外字段比如自定义字段不在类型定义中
      ...rest
    } = props

    Object.assign(this, rest)

    this.taskId = _taskId
    this.activityId = _activityId
    this.rid = _rid
    this.reflectionDomain = _reflectionDomain
    this.completedStatus = _completedStatus
    this.reviewedStatus = _reviewedStatus
    this.startAt = _startAt
    this.endAt = _endAt
    this.duration = _duration
    this.gainedScore = _gainedScore
    this.gainedScoreRate = _gainedScoreRate
    this.setting = _setting ? new TaskReflectionSetting(_setting) : _setting
    this.reflection = _reflection
      ? new ForegroundReflection(_reflection)
      : _reflection
    this.completedTaskSum = _completedTaskSum
    this.taskSum = _taskSum
    this.completedDetails = _completedDetails
      ? _completedDetails.map((i) => new TaskCompletedDetail(i))
      : _completedDetails
    this.enrollOptions = _enrollOptions
      ? _enrollOptions.map((i) => new Option(i))
      : _enrollOptions
    this.questionnaire = _questionnaire
      ? _questionnaire.map((i) => new QuestionnaireValueRes(i))
      : _questionnaire
    this.quizBlockCount = _quizBlockCount
    this.interviewResult = _interviewResult
      ? new InterviewReflection(_interviewResult)
      : _interviewResult
    this.id = _id
    this.schoolId = _schoolId
    this.createdAt = _createdAt
    this.updatedAt = _updatedAt
  }
}
